import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["muted"]
const _hoisted_2 = {
  key: 0,
  class: "mic-off-badge",
  "aria-label": "Microfone desligado"
}
const _hoisted_3 = { class: "label-text" }
const _hoisted_4 = { class: "role" }
const _hoisted_5 = { class: "name" }

import { computed, ref, watch, onMounted, getCurrentInstance } from "vue";

interface PipPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  variant: "local" | "remote";
  stream?: MediaStream | null;
  role?: string;
  name?: string;
  label?: string;
  showSelf?: boolean;
  audioMuted?: boolean;
  pipPosition?: PipPosition;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoTile',
  props: {
    variant: {},
    stream: { default: null },
    role: { default: "" },
    name: { default: "" },
    label: { default: "Você" },
    showSelf: { type: Boolean, default: false },
    audioMuted: { type: Boolean, default: false },
    pipPosition: { default: () => ({ x: 0, y: 0, w: 260, h: 160 }) }
  },
  emits: ["update:pipPosition", "swap"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const instance = getCurrentInstance();
const $Session = instance?.appContext.config.globalProperties.$Session;
const $userSessionName =
  instance?.appContext.config.globalProperties.$userSessionName;

const professional = ref<{ name: string; role: string } | null>(null);

const displayName = computed(() => {
  if (props.name) return props.name;
  return professional.value?.name || "Profissional";
});

const displayRole = computed(() => {
  if (props.role) return props.role;
  return professional.value?.role || "Profissional";
});

function loadProfessionalData() {
  if (!$Session || !$userSessionName) return;

  const session = $Session.get($userSessionName);
  if (!session) return;

  professional.value = {
    name:
      session.professional?.Name ||
      session.professional?.name ||
      "Profissional",
    role: "Profissional"
  };
}

const videoElement = ref<HTMLVideoElement | null>(null);

const pipStyle = computed(() => {
  if (props.variant !== "local") return {};
  return {
    transform: `translate(${props.pipPosition.x}px, ${props.pipPosition.y}px)`,
    width: `${props.pipPosition.w}px`,
    height: `${props.pipPosition.h}px`
  };
});

watch(
  () => props.stream,
  (newStream) => {
    if (videoElement.value) {
      videoElement.value.srcObject = newStream;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (videoElement.value && props.stream) {
    videoElement.value.srcObject = props.stream;
  }

  if (!props.showSelf) {
    loadProfessionalData();
  }
});

function onDragStart(e: PointerEvent) {
  if ((e.target as HTMLElement).classList.contains("pip-resize-handle")) return;

  const target = e.currentTarget as HTMLElement;
  target.setPointerCapture(e.pointerId);

  const startX = e.clientX;
  const startY = e.clientY;
  const startLeft = props.pipPosition.x;
  const startTop = props.pipPosition.y;

  const onMove = (ev: PointerEvent) => {
    const newX = startLeft + (ev.clientX - startX);
    const newY = startTop + (ev.clientY - startY);
    emit("update:pipPosition", {
      ...props.pipPosition,
      x: newX,
      y: newY
    });
  };

  const onUp = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
}

function onResizeStart(e: PointerEvent) {
  const startX = e.clientX;
  const startW = props.pipPosition.w;

  const onMove = (ev: PointerEvent) => {
    const newW = Math.max(180, startW + (ev.clientX - startX));
    const newH = newW * (10 / 16);
    emit("update:pipPosition", {
      ...props.pipPosition,
      w: newW,
      h: newH
    });
  };

  const onUp = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };

  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
}

__expose({
  videoElement
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["video-wrapper", [_ctx.variant, { clickable: _ctx.variant === 'local' }]]),
    style: _normalizeStyle(_ctx.variant === 'local' ? pipStyle.value : undefined),
    onPointerdown: _cache[0] || (_cache[0] = ($event: any) => (_ctx.variant === 'local' ? onDragStart($event) : undefined)),
    onDblclick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('swap')))
  }, [
    _createElementVNode("video", {
      ref_key: "videoElement",
      ref: videoElement,
      autoplay: "",
      playsinline: "",
      muted: _ctx.showSelf
    }, null, 8, _hoisted_1),
    _createElementVNode("div", {
      class: _normalizeClass(["user-label", { mini: _ctx.variant === 'local' }])
    }, [
      (_ctx.audioMuted)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, _cache[2] || (_cache[2] = [
            _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              fill: "currentColor"
            }, [
              _createElementVNode("path", { d: "M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.17l5.98 6zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .42-.03.62-.08l4.09 4.09c-.84.54-1.84.85-2.93.92V21h-2v-3.08c-3.39-.49-5.99-3.39-5.99-6.92H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c1.22-.18 2.36-.61 3.33-1.24l3.4 3.4L21 18.73 4.27 3z" })
            ], -1)
          ])))
        : _createCommentVNode("", true),
      _createElementVNode("span", _hoisted_3, [
        (_ctx.showSelf)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(_toDisplayString(_ctx.label || "Você"), 1)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createElementVNode("span", _hoisted_4, _toDisplayString(displayRole.value), 1),
              _createElementVNode("span", _hoisted_5, _toDisplayString(displayName.value), 1)
            ], 64))
      ])
    ], 2),
    (_ctx.variant === 'local')
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "pip-resize-handle",
          onPointerdown: _withModifiers(onResizeStart, ["stop"])
        }, null, 32))
      : _createCommentVNode("", true)
  ], 38))
}
}

})